import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { LOCALE_DIRECTION, SupportedLocale, type TextDirection } from '@optiflow/types';
import en from './locales/en.json';
import ar from './locales/ar.json';
import { formatCount } from '@/utils/format';

export const SUPPORTED_LOCALES: SupportedLocale[] = [SupportedLocale.AR, SupportedLocale.EN];

const DEFAULT_LOCALE: SupportedLocale =
  (import.meta.env.VITE_DEFAULT_LOCALE as SupportedLocale | undefined) ?? SupportedLocale.AR;

/** Where a returning user's language comes from: their own saved choice, else the page's Arabic default; the browser's language is deliberately not consulted, so a first visit always opens in Arabic. */
export const LOCALE_DETECTION_ORDER = ['localStorage', 'htmlTag'];

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as string[]).includes(value);
}

/**
 * Reduces any BCP 47 tag to a locale the product ships.
 *
 * Detection returns region-tagged values such as `en-US`, which are not themselves supported
 * locales. Comparing them directly would silently fall back to the default and leave the document
 * in the wrong direction while i18next served the right translations.
 */
export function normalizeLocale(value: string | undefined): SupportedLocale {
  const base = value?.split('-')[0]?.toLowerCase() ?? '';
  return isSupportedLocale(base) ? base : DEFAULT_LOCALE;
}

export function directionFor(locale: SupportedLocale): TextDirection {
  return LOCALE_DIRECTION[locale];
}

/**
 * Reflects the active locale onto the document.
 *
 * Direction is a document-level concern: setting `dir` here is what makes every logical Tailwind
 * utility (`ms-*`, `pe-*`, `text-start`) flip, which is why no component hard-codes left or right.
 */
export function applyDocumentLocale(locale: SupportedLocale): void {
  const direction = directionFor(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = direction;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: LOCALE_DETECTION_ORDER,
      lookupLocalStorage: 'optiflow.locale',
      caches: ['localStorage'],
    },
  });

// A count inside a sentence takes the digits money and dates take in the same language, so an Arabic line never mixes two sets. Used as {{count, count}}.
i18n.services.formatter?.add('count', (value: unknown, language: string | undefined) =>
  formatCount(Number(value), normalizeLocale(language)),
);

i18n.on('languageChanged', (language: string) => {
  applyDocumentLocale(normalizeLocale(language));
});

applyDocumentLocale(normalizeLocale(i18n.resolvedLanguage ?? i18n.language));

export default i18n;
